// src/components/PostForm.jsx
// Pinch Point submission form.
// Desktop sidebar variant + standalone variant. AI rewrite + suggested fields.
//
// Props (matching current useAuth/usePosts shape):
//   user, profile, guidelines, categories, locations, defaultLocationId
//   onSubmit(data)          // -> Firestore addPost
//   onAfterSubmit(msg, isErr)
//   variant: "sidebar" | "modal" | "page"
//   greeting: optional small header text shown above the form

import React, { useState, useEffect, useRef} from "react";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { DEFAULT_GUIDELINES} from "../hooks/useOrgSettings";
import { callClaude, MODELS } from "../lib/claudeApi";
import * as PPIcons from "./_icons";
import { Button, Chip, Field, SeverityRadio } from "./_primitives";

function getCallable(name) {
  return httpsCallable(getFunctions(getApp()), name);
}

async function moderatePost(description, category, severity, guidelines) {
  const system = `You are a content moderator for a business operations tool called Pinch Point.
Posts must follow these guidelines:
${guidelines}

Respond ONLY with valid JSON in this exact shape:
{"approved": true|false, "reason": "short reason if rejected"}

Approve if the post is a genuine operational or business issue described constructively without naming individuals.
Reject if: it names or clearly targets a specific person, is purely a personal complaint with no operational context, or contains inappropriate language.`;
  try {
    const text = await callClaude(
      system,
      `Category: ${category}\nSeverity: ${severity}\nDescription: ${description}`,
      { model: MODELS.INLINE_UX, maxTokens: 1000 }
    );
    const clean = text.replace(/```json|```/g, "").trim();
    return JSON.parse(clean);
  } catch {
    return { approved: true };
  }
}

async function rewritePost(description, category, severity, guidelines, examples = []) {
  let examplesSection = "";
  if (examples.length > 0) {
    examplesSection =
      "\n\nThis user has previously edited AI rewrites to better match their style. Apply a similar tone and level of detail:\n" +
      examples
        .map((e, i) => `Example ${i + 1}:\n  Original: "${e.original}"\n  How this user prefers it: "${e.final}"`)
        .join("\n");
  }
  const system = `You are helping an operations team member rewrite a pinch point report to be clearer, more specific, and more actionable.
Submission guidelines:\n${guidelines || "Be factual and constructive."}

Rules:
- Keep the same facts, don't add new ones
- Make it specific: include location, impact, and what needs to happen
- Do NOT name specific people
- Return ONLY the rewritten text, nothing else${examplesSection}`;
  return await callClaude(
    system,
    `Category: ${category}\nSeverity: ${severity}\nOriginal: ${description}`,
    { model: MODELS.INLINE_UX, maxTokens: 400 }
  );
}

function PostForm({
  user, profile,
  guidelines = [],
  categories = [],
  locations = [],
  defaultLocationId,
  onSubmit, onAfterSubmit,
  variant = "sidebar",
  greeting,
}) {
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [severity, setSeverity] = useState("");
  const [locationId, setLocationId] = useState(defaultLocationId || "");
  const [shift, setShift] = useState("");
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiSuggestLoading, setAiSuggestLoading] = useState(false);
  const [aiRewrite, setAiRewrite] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [moderationError, setModerationError] = useState("");
  const [rulesOpen, setRulesOpen] = useState(false);

  const debounceRef = useRef(null);
  const categoriesRef = useRef(categories);
  categoriesRef.current = categories;

  const orgId = profile?.orgId;
  const guidelinesText = Array.isArray(guidelines) ? guidelines.join("\n") : guidelines || "";

  const charCount = description.length;
  const canSubmit = description.trim().length >= 10 && category && severity && !busy && !aiLoading;

  // Debounced AI field suggestions
  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (description.length <= 20 || !orgId) {
      setAiSuggestion(null);
      setAiSuggestLoading(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setAiSuggestLoading(true);
      try {
        const fn = getCallable("suggestPostFields");
        const result = await fn({ text: description, orgId });
        const suggestion = result.data;
        setAiSuggestion(suggestion);
        if (suggestion.severity) setSeverity(suggestion.severity);
        if (suggestion.category) {
          const match = categoriesRef.current.find(
            (c) => c.toLowerCase() === suggestion.category.toLowerCase()
          );
          if (match) setCategory(match);
        }
      } catch {
        // suggestions are best-effort
      } finally {
        setAiSuggestLoading(false);
      }
    }, 800);
    return () => clearTimeout(debounceRef.current);
  }, [description, orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAiRewrite = async () => {
    if (!description.trim()) return;
    setAiLoading(true);
    try {
      let examples = [];
      if (orgId) {
        try {
          const fn = getCallable("getRewriteExamples");
          const result = await fn({ orgId, description });
          examples = result.data?.examples || [];
        } catch {
          // best-effort
        }
      }
      const suggested = await rewritePost(description, category || "General", severity, guidelinesText, examples);
      setAiRewrite({ original: description, suggested: suggested.trim() });
    } catch {
      onAfterSubmit?.("AI rewrite failed — check connection.", true);
    } finally {
      setAiLoading(false);
    }
  };

  const acceptSuggestion = () => {
    if (!aiSuggestion) return;
    if (aiSuggestion.category) {
      const match = categories.find((c) => c.toLowerCase() === aiSuggestion.category.toLowerCase());
      if (match) setCategory(match);
    }
    if (aiSuggestion.severity) setSeverity(aiSuggestion.severity);
  };

  const acceptRewrite = async () => {
    if (!aiRewrite) return;
    const finalText = aiRewrite.suggested;
    setDescription(finalText);
    setAiRewrite(null);
    if (orgId) {
      try {
        const fn = getCallable("logRewriteFeedback");
        await fn({ orgId, originalText: aiRewrite.original, suggestedText: aiRewrite.suggested, finalText });
      } catch {
        // best-effort
      }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setModerationError("");
    if (!category) { onAfterSubmit?.("Please select a category.", true); return; }

    setBusy(true);
    try {
      const verdict = await moderatePost(description.trim(), category, severity, guidelinesText);
      if (!verdict.approved) {
        setModerationError(
          "This post does not comply with submission guidelines." +
          (verdict.reason ? ` ${verdict.reason}` : "")
        );
        setBusy(false);
        return;
      }
      await onSubmit({
        description: description.trim(),
        category, severity,
        locationId: locationId || null,
        shift: shift || null,
        authorId: user?.uid || null,
        authorName: profile?.displayName || user?.displayName || user?.email,
        anonymous: false,
        ...(aiSuggestion && category === aiSuggestion.category && severity === aiSuggestion.severity ? {
          aiSuggestedCategory: aiSuggestion.category,
          aiSuggestedSeverity: aiSuggestion.severity,
          aiSuggestionConfidence: aiSuggestion.confidence,
        } : {}),
      });
      setDescription(""); setCategory(""); setSeverity(""); setShift("");
      setAiSuggestion(null); setAiRewrite(null); setModerationError("");
      onAfterSubmit?.("Pinch point submitted.");
    } catch {
      onAfterSubmit?.("Could not submit — try again.", true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="pp-postform" onSubmit={submit} data-variant={variant}>
      {greeting && (
        <div style={{ fontSize: 13, color: "var(--text-dim)", lineHeight: 1.5 }}>
          {greeting}
        </div>
      )}

      {/* Description with floating AI assist */}
      <Field
        label="What's the pinch point?"
        hint={
          description.length > 0
            ? `${charCount} characters · be specific about location and impact`
            : "Describe a workflow friction point. Be specific, factual, constructive."
        }
      >
        <div style={{ position: "relative" }}>
          <textarea
            className="pp-textarea"
            placeholder="e.g. South Lobby door closer has lost tension, leaving the door propped open ~6 inches and bleeding HVAC."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={variant === "modal" ? 4 : 6}
            style={{ minHeight: variant === "modal" ? 110 : 140 }}
          />
          <div style={{ position: "absolute", bottom: 8, right: 8, display: "flex", gap: 6 }}>
            {description.length >= 20 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                icon={<PPIcons.IconSparkle />}
                onClick={handleAiRewrite}
                disabled={busy || aiLoading}
              >
                {aiLoading ? "Polishing…" : "Polish"}
              </Button>
            )}
          </div>
        </div>
      </Field>

      {/* AI rewrite review */}
      {aiRewrite && (
        <div style={{
          background: "var(--accent-soft)", border: "1px solid var(--accent-soft-bd)",
          borderRadius: "var(--radius)", padding: 12,
          display: "flex", flexDirection: "column", gap: 8,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Chip tone="ai" dot={false}><PPIcons.IconSparkle style={{ width: 11, height: 11 }} /> AI suggestion</Chip>
            <span style={{ fontSize: 12, color: "var(--text-dim)" }}>Clearer, more actionable</span>
          </div>
          <p style={{ fontSize: 13, lineHeight: 1.5, color: "var(--text)" }}>{aiRewrite.suggested}</p>
          <div style={{ display: "flex", gap: 6 }}>
            <Button type="button" variant="primary" size="sm" onClick={acceptRewrite}>Use this</Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setAiRewrite(null)}>Keep mine</Button>
          </div>
        </div>
      )}

      {/* AI auto-suggest field row */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {aiSuggestLoading ? (
          <span style={{ fontSize: 12, color: "var(--text-faint)", display: "flex", alignItems: "center", gap: 6 }}>
            <PPIcons.IconSparkle style={{ width: 12, height: 12 }} /> Suggesting…
          </span>
        ) : aiSuggestion ? (
          <div style={{
            flex: 1, display: "flex", alignItems: "center", gap: 8,
            padding: "8px 10px", background: "var(--accent-soft)",
            border: "1px solid var(--accent-soft-bd)", borderRadius: "var(--radius-sm)",
            fontSize: 12,
          }}>
            <PPIcons.IconSparkle style={{ width: 12, height: 12, color: "var(--accent)" }} />
            <span style={{ color: "var(--text)" }}>
              <strong>{aiSuggestion.category}</strong> · <strong>{aiSuggestion.severity}</strong>
              {aiSuggestion.confidence != null && (
                <span style={{ color: "var(--text-faint)", marginLeft: 6, fontFamily: "var(--font-mono)" }}>
                  {Math.round(aiSuggestion.confidence * 100)}%
                </span>
              )}
            </span>
            <button
              type="button"
              onClick={acceptSuggestion}
              style={{
                marginLeft: "auto",
                background: "transparent", border: 0, color: "var(--accent)",
                fontWeight: 700, cursor: "pointer", fontSize: 12,
              }}
            >
              Apply
            </button>
          </div>
        ) : null}
      </div>

      {/* Moderation error */}
      {moderationError && (
        <div style={{
          padding: "8px 12px", background: "var(--pp-crit-bg)",
          border: "1px solid var(--pp-crit)", borderRadius: "var(--radius-sm)",
          fontSize: 12, color: "var(--pp-crit)",
        }}>
          {moderationError}
        </div>
      )}

      {/* Category */}
      <Field label="Category">
        <select className="pp-select" value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">— Select category —</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>

      {/* Severity */}
      <Field label="Severity" hint="How much does this impact the operation?">
        <SeverityRadio value={severity} onChange={setSeverity} />
      </Field>

      {/* Location + shift */}
      {(locations.length > 0 || variant !== "modal") && (
        <div style={{ display: "grid", gridTemplateColumns: locations.length ? "1fr 1fr" : "1fr", gap: 8 }}>
          {locations.length > 0 && (
            <Field label="Location">
              <select className="pp-select" value={locationId} onChange={(e) => setLocationId(e.target.value)}>
                <option value="">All / unspecified</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </Field>
          )}
          <Field label="Shift">
            <select className="pp-select" value={shift} onChange={(e) => setShift(e.target.value)}>
              <option value="">—</option>
              <option value="morning">Morning</option>
              <option value="afternoon">Afternoon</option>
              <option value="evening">Evening</option>
              <option value="overnight">Overnight</option>
            </select>
          </Field>
        </div>
      )}

      {/* Submit */}
      <Button variant="primary" size="lg" type="submit" disabled={!canSubmit}>
        {busy ? "Submitting…" : "Submit pinch point"}
        {!busy && <PPIcons.IconArrowUp style={{ transform: "rotate(45deg)" }} />}
      </Button>

      {/* Guidelines disclosure */}
      {guidelines.length > 0 && (
        <details
          open={rulesOpen}
          onToggle={(e) => setRulesOpen(e.target.open)}
          style={{
            background: "var(--surface-2)", border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)", padding: "10px 12px",
            fontSize: 12,
          }}
        >
          <summary style={{
            cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
            fontWeight: 700, color: "var(--text-dim)", listStyle: "none",
          }}>
            <PPIcons.IconShield style={{ width: 12, height: 12 }} />
            Submission guidelines
            <PPIcons.IconChevD style={{ width: 11, height: 11, marginLeft: "auto",
              transform: rulesOpen ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
          </summary>
          <ol style={{
            margin: "10px 0 0 18px", padding: 0, color: "var(--text-dim)",
            display: "flex", flexDirection: "column", gap: 6, lineHeight: 1.5,
          }}>
            {(Array.isArray(guidelines) ? guidelines : guidelinesText.split(/\r?\n/).filter(g => g.trim())).map((g, i) => <li key={i}>{g}</li>)}
          </ol>
        </details>
      )}
    </form>
  );
}

export default PostForm;
